import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";
import "./Pagination.css";

interface PaginationProps {
  disabled?: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  resetPageSize: (pageNumber: number, pageSize: number) => void;
}
const pageSizes = [10, 25, 50, 100];
const Pagination: React.FC<PaginationProps> = ({ disabled = false, currentPage, totalPages, onPageChange, pageSize, resetPageSize }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeOption, setActiveOption] = useState(0);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();
  const closeMenu = (returnFocus = false) => {
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus({ preventScroll: true });
  };
  const openMenu = (last = false) => {
    if (disabled) return;
    setActiveOption(last ? pageSizes.length - 1 : Math.max(pageSizes.indexOf(pageSize), 0));
    setIsOpen(true);
  };
  useLayoutEffect(() => {
    if (!isOpen) return;
    const positionMenu = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const margin = 8, gap = 6, desiredHeight = 172;
      const below = Math.max(0, window.innerHeight - rect.bottom - margin - gap);
      const above = Math.max(0, rect.top - margin - gap);
      const opensAbove = below < desiredHeight && above > below;
      const width = Math.min(Math.max(rect.width, 96), window.innerWidth - margin * 2);
      setMenuStyle({ position: "fixed", width, maxHeight: Math.min(desiredHeight, opensAbove ? above : below),
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        ...(opensAbove ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }) });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => { window.removeEventListener("resize", positionMenu); window.removeEventListener("scroll", positionMenu, true); };
  }, [isOpen]);
  useLayoutEffect(() => {
    if (!isOpen) return;
    const option = optionRefs.current[activeOption];
    const menu = menuRef.current;
    option?.focus({ preventScroll: true });
    // Reveal keyboard selection inside a constrained menu without scrolling the page.
    if (option && menu) {
      if (option.offsetTop < menu.scrollTop) menu.scrollTop = option.offsetTop;
      else if (option.offsetTop + option.offsetHeight > menu.scrollTop + menu.clientHeight)
        menu.scrollTop = option.offsetTop + option.offsetHeight - menu.clientHeight;
    }
  }, [isOpen, activeOption]);
  useEffect(() => {
    if (!isOpen) return;
    const outsideClick = (event: MouseEvent) => {
      if (!triggerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", outsideClick);
    return () => document.removeEventListener("mousedown", outsideClick);
  }, [isOpen]);
  useEffect(() => { if (disabled) setIsOpen(false); }, [disabled]);
  const menuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") { event.preventDefault(); closeMenu(true); }
    else if (event.key === "Tab") closeMenu(true);
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActiveOption((index) => (index + (event.key === "ArrowDown" ? 1 : -1) + pageSizes.length) % pageSizes.length);
    } else if (event.key === "Home" || event.key === "End") { event.preventDefault(); setActiveOption(event.key === "Home" ? 0 : pageSizes.length - 1); }
  };
  const changePage = (page: number) => { if (!disabled && page > 0 && page <= totalPages) onPageChange(page); };
  return (
    <div className="waivera-pagination pagination_flx flex flex-wrap items-center justify-between w-full gap-4">
      <div className="pagesize_flx flex items-center gap-3">
        <span className="waivera-pagination-label text-sm font-semibold">Show</span>
        <button type="button" ref={triggerRef} disabled={disabled} aria-label="Entries per page" aria-haspopup="menu"
          aria-expanded={isOpen} aria-controls={isOpen ? menuId : undefined}
          onClick={() => isOpen ? closeMenu() : openMenu()}
          onKeyDown={(event) => { if (event.key === "ArrowDown" || event.key === "ArrowUp") { event.preventDefault(); openMenu(event.key === "ArrowUp"); } }}
          className="waivera-pagination-select">
          <span>{pageSize}</span><ChevronDown size={16} aria-hidden="true" className={isOpen ? "is-open" : ""} />
        </button>
        <span className="waivera-pagination-label text-sm font-semibold">Entries</span>
      </div>
      <div className="pagination_right flex flex-wrap items-center gap-4">
        <span className="text-sm"><span className="waivera-pagination-label font-semibold">Page:</span>{" "}{currentPage.toString().padStart(2, "0")} of {totalPages}</span>
        <div className="waivera-pagination-controls">
          <button type="button" aria-label="Previous page" onClick={() => changePage(currentPage - 1)} disabled={disabled || currentPage <= 1} className="waivera-pagination-arrow"><ArrowLeft size={18} aria-hidden="true" /></button>
          <button type="button" aria-label="Next page" onClick={() => changePage(currentPage + 1)} disabled={disabled || currentPage >= totalPages} className="waivera-pagination-arrow"><ArrowRight size={18} aria-hidden="true" /></button>
        </div>
      </div>
      {isOpen && createPortal(
        <div ref={menuRef} id={menuId} role="menu" aria-label="Entries per page" className="waivera-pagination-menu" style={menuStyle} onKeyDown={menuKeyDown}>
          {pageSizes.map((size, index) => (
            <button type="button" role="menuitemradio" aria-checked={pageSize === size} ref={(element) => { optionRefs.current[index] = element; }}
              tabIndex={index === activeOption ? 0 : -1} disabled={disabled} key={size}
              onClick={() => { if (!disabled) { resetPageSize(1, size); closeMenu(true); } }}
              className={`waivera-pagination-option${pageSize === size ? " is-selected" : ""}`}>{size}</button>
          ))}
        </div>, document.body,
      )}
    </div>
  );
};
export default Pagination;
